import {
  Alert,
  Button,
  Flex,
  Heading,
  Modal,
  RadioInput,
  RadioInputGroup,
  ScreenReaderContent,
  Text,
  TextArea,
  TextInput,
} from "@instructure/ui";
import { IconAddLine } from "@instructure/ui-icons";
import { useTranslation } from "react-i18next";
import { LearningOutcomesService } from "../../../services/LearningOutcomesService";

import { useEffect, useRef, useState } from "react";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";

function InlineOutcomeCreator(props) {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const shouldShow = props.shouldShow;
  const setShouldShow = props.setShouldShow;
  const lengthOfOutcomes = props.outcomeLength;
  const refreshFunc = props.refreshFunction;
  const spinnerSetter = props.spinnerState;
  const program = props.academicSubject;
  const loType = props.loType;
  const scrollToBottom = props.scrollToBottomFunc;
  const [isButtonPressed, setButtonPressed] = useState(false);

  useEffect(() => {
    if (shouldShow) {
      setButtonPressed(false);
      scrollToBottom();
    }
  }, [shouldShow]);

  const saveOutcome = async (outcomeName, outcomeStatement, variety, index) => {
    await LearningOutcomesService.createLearningOutcome({
      name: outcomeName,
      seq: index,
      statement: outcomeStatement,
      outcomeType: loType,
      academicSubject: loType === "PSLO" ? program : "",
      programId: program ?? "",
      outcomeVariety: variety,
    }).catch(() => {
      showNotification(
        t("notifications.general.createError", { obj: "outcome" }),
        ERROR
      );
    });
    refreshFunc();
  };

  const InlineEditor = () => {
    const bottomRef = useRef();
    const [titleText, setTitleText] = useState("");
    const [statementText, setStatementText] = useState("");
    const [isWarningOpen, setIsWarningOpen] = useState(false);
    const [variety, setVariety] = useState("outcome");

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          margin: "0px",
        }}
      >
        <Modal.Header
          style={{ padding: "0px !important" }}
          spacing="compact"
          themeOverride={{
            paddingCompact: "8px 8px 6px",
            borderColor: "transparent",
          }}
        >
          <Heading level="h5" margin="0">
            <Text size="small" weight="bold">
              {t("categories.createNewOutcome")}
            </Text>
          </Heading>
        </Modal.Header>
        <Modal.Body padding="xx-small">
          <Flex gap="xx-small" wrap="wrap">
            <Flex.Item width="100%" shouldGrow>
              <TextInput
                required={true}
                onChange={(e) => {
                  setTitleText(e.target.value);
                  setIsWarningOpen(false);
                }}
                messages={
                  titleText === "" && isButtonPressed
                    ? [
                        {
                          text: (
                            <p
                              style={{
                                fontSize: "0.75rem",
                                marginTop: "-10px",
                              }}
                            >
                              {t("programs.iloTitleRequired")}
                            </p>
                          ),
                          type: "error",
                        },
                      ]
                    : []
                }
                size="small"
                renderLabel={
                  <ScreenReaderContent>
                    {t("categories.outcomeName")}
                  </ScreenReaderContent>
                }
                placeholder={t("categories.outcomeName")}
              />
            </Flex.Item>
            <Flex.Item width="100%" shouldGrow>
              <TextArea
                required={true}
                messages={
                  statementText === "" && isButtonPressed
                    ? [
                        {
                          text: (
                            <p
                              style={{
                                fontSize: "0.75rem",
                                marginTop: "-10px",
                              }}
                            >
                              {t("programs.iloStatementRequired")}
                            </p>
                          ),
                          type: "error",
                        },
                      ]
                    : []
                }
                size="small"
                onChange={(e) => {
                  setStatementText(e.target.value);
                  setIsWarningOpen(false);
                }}
                label={
                  <ScreenReaderContent>
                    {t("categories.outcomeStatement")}
                  </ScreenReaderContent>
                }
                autoGrow={true}
                placeholder={t("categories.outcomeStatement")}
              />
            </Flex.Item>
          </Flex>
        </Modal.Body>

        {isWarningOpen ? (
          <div style={{ width: "100%", height: "100%" }}>
            <Alert
              variant="warning"
              ref={bottomRef}
              margin="none xx-small x-small"
              themeOverride={{ color: "#ff5823", contentFontWeight: "bold" }}
              hasShadow={false}
            >
              {!titleText ? t("categories.titleText") : ""}{" "}
              {!titleText && !statementText ? "and " : "The"}{" "}
              {!statementText ? t("categories.statementText") : ""}{" "}
              {t("categories.cannotBeEmpty")}
            </Alert>
          </div>
        ) : (
          <div style={{ height: "1px", width: "100px" }} ref={bottomRef} />
        )}
        <Flex justifyItems="end" padding="xx-small">
          <Flex.Item shouldGrow>
            <RadioInputGroup
              size="small"
              layout="columns"
              name="outcome_variety"
              value={variety}
              onChange={(_e, val) => setVariety(val)}
              description={
                <ScreenReaderContent>
                  {t("learningOutcomes.outcomeVariety")}
                </ScreenReaderContent>
              }
            >
              <RadioInput value="outcome" label={t("ratingScale.lo")} />
              <RadioInput
                value="standard"
                label={t("learningOutcomes.standard")}
              />
            </RadioInputGroup>
          </Flex.Item>
          <Flex.Item>
            <Button
              size="small"
              onClick={() => setShouldShow(false)}
              margin="0 xx-small"
            >
              {t("global.cancel")}
            </Button>
          </Flex.Item>
          <Flex.Item>
            <Button
              size="small"
              renderIcon={IconAddLine}
              withBackground={false}
              color="primary"
              onClick={() => {
                if (titleText === "" || statementText === "") {
                  setIsWarningOpen(true);
                  setButtonPressed(true);
                  return;
                } else {
                  spinnerSetter(true);
                  saveOutcome(
                    titleText,
                    statementText,
                    variety,
                    lengthOfOutcomes - 1
                  );
                  setShouldShow(false);
                }
              }}
            >
              {t("global.add")}
            </Button>
          </Flex.Item>
        </Flex>
      </div>
    );
  };

  return (
    <div>
      {shouldShow && (
        <div
          style={{ width: "100%", height: "1.5px", backgroundColor: "#c7cdd1" }}
        />
      )}
      <div
        id="LabelTesting"
        style={{
          padding: shouldShow ? "0px" : "10px",
          borderRadius: "5px",
          marginInline: shouldShow ? "-5px" : "0px",
        }}
      >
        {shouldShow && <InlineEditor />}
      </div>
    </div>
  );
}

export default InlineOutcomeCreator;
